import {ref, watch} from 'vue';
import {
    changePassword,
    createTalent, deleteUser, fetchAdmins,
    fetchMe,
    fetchMeAddress, fetchSearchUsers,
    fetchUserAddress,
    fetchUserData,
    fetchUsers, fetchUsersAdmin, fetchUsersCategory, fetchUsersCategoryAdmin, fetchUserToken,
    login as loginApi,
    patchMe,
    patchUser, resetEmail, updateUserOrders
} from '~/api/userApi';
import {postContact} from "~/api/authApi";
import type {User} from '~/types/types';

const users = ref<User[]>([]);

export function useUserStore() {
    // Define variables to hold the state
    let user, token, visitingToken, visitingUser, showLayout;
    
    // Only use useState on the client side
    if (process.client) {
        try {
            user = useState<User | null>('user', () => null);
            token = useState<string>('token', () => '');
            visitingToken = useState<string>('visiting_token', () => '');
            visitingUser = useState<User | null>('visiting_user', () => null);
            showLayout = useState<boolean>('showLayout', () => false);
            // Initialize token from localStorage if available
            if (!token.value) {
                token.value = localStorage.getItem('token') || '';
            }
        } catch (error) {
            console.error('Failed to initialize useState in useUserStore:', error);
            // Fallback to ref if useState fails
            user = ref<User | null>(null);
            token = ref(localStorage.getItem('token') || '');
            visitingToken = ref('');
            visitingUser = ref<User | null>(null);
            showLayout = ref(false);
        }
    } else {
        // Server-side fallbacks
        user = ref<User | null>(null);
        token = ref('');
        visitingToken = ref('');
        visitingUser = ref<User | null>(null);
        showLayout = ref(false);
    }

    const setToken = (newToken: string) => {
        token.value = newToken;
        if (process.client) {
            localStorage.setItem('token', newToken);
        }
    };

    const setVisiting = (newToken: string, user: any) => {
        visitingToken.value = newToken;
        visitingUser.value = user;
        if (process.client) {
            localStorage.setItem('visiting_token', newToken);
            localStorage.setItem('visiting_user', user);
        }
    };

    const setShowLayout = (newShowLayout: boolean) => {
        showLayout.value = newShowLayout;
    }

    const fetchUser = async () => {
        if (!token.value) {
            return;
        }
        try {
            user.value = await fetchMe(token.value);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            // If fetching the user fails, clear the token
            setToken('');
        }
    };

    const logOut = () => {
        user.value = null;
        setToken('');
    }

    const fetchUserProfile = async (id: string) => {

        try {
            const result = await fetchUserData(id);
            return result;
        } catch (error) {
            console.error('Failed to fetch user:', error);
            // If fetching the user fails, clear the token
            setToken('');
        }
    };

    const fetchUserAddresses = async () => {
        if (!token.value) {
            return;
        }

        try {
            return await fetchMeAddress(token.value);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            // If fetching the user fails, clear the token
            setToken('');
        }
    };

    const fetchVisitingUserAddresses = async (id: string) => {
        if (!token.value) {
            return;
        }

        try {
            return await fetchUserAddress(token.value, id);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            // If fetching the user fails, clear the token
            setToken('');
        }
    };

    const updateUserOrder = async (updatedUsers: any[]) => {
        try {
            const response = await updateUserOrders({users: updatedUsers}, token.value);
            return response;
        } catch (error) {
            console.error('Failed to update tag order:', error);
        }
    };
    const loadUsers = async (query: Record<string, any> = {}) => {
        try {
            const response = await fetchUsers(query); // Pass the query object to fetchUsers
            users.value = response; // Store the response in users state
            return response; // Return the response for further use if needed
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error; // Optionally rethrow the error for handling upstream
        }
    };

    const loadUsersAdmin = async (query: Record<string, any> = {}) => {
        try {
            const response = await fetchUsersAdmin(query); // Pass the query object to fetchUsers
            return response; // Return the response for further use if needed
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error; // Optionally rethrow the error for handling upstream
        }
    };

    const loadUsersByCategory = async (role: string, category?: string, status?: string) => {
        try {
            const response = await fetchUsersCategory(role, category, status);
            users.value = response;
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadUsersByCategoryAdmin = async (role: string, category?: string, status?: string) => {
        try {
            const response = await fetchUsersCategoryAdmin(role, category, status);
            users.value = response;
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const searchUsers = async (search?: string) => {
        try {
            const response = await fetchSearchUsers(search) as any;
            users.value = response;
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };
    const loadAdmins = async () => {
        try {
            const response = await fetchAdmins();
            return response;
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const updateMe = async (payload: any) => {
        try {
            return await patchMe(user.value?._id || '', payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };

    const updateUser = async (payload: any, id: string) => {
        try {
            return await patchUser(id, payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };

    const newTalent = async (payload: any) => {
        try {
            return await createTalent(payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };

    const editTalent = async (payload: any, id: string) => {
        try {
            return await patchUser(id, payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };
    const deleteTalent = async (id: string) => {
        try {
            return await deleteUser(id, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };

    const login = async (email: string, password: string) => {
        try {
            const result = await loginApi(email, password);
            setToken(result.token);
            console.log('result', result.token);
            await fetchUser();
            return true;
        } catch (error) {
            console.error('Failed to login:', error);
            return false;
        }
    };

    const getTokenForUser = async (id: string) => {
        try {
            const result = await fetchUserToken(token.value, id);
            console.log('token data', result);
            setVisiting(result.token, result.user);
            return result;
        } catch (error) {
            console.error('Failed to login:', error);
            return false;
        }
    };

    const newContact = async (payload: any) => {
        try {
            return await postContact(payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };
    const changeOldPassword = async (payload: any) => {
        try {
            return await changePassword(payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };
    const changeEmail = async (payload: any) => {
        try {
            return await resetEmail(payload, token.value);
        } catch (error) {
            console.error('Failed to load users:', error);
            throw error;
        }
    };
    // Watch for changes in token and update localStorage
    watch(token, (newToken) => {
        if (process.client) {
            if (newToken) {
                localStorage.setItem('token', newToken);
            } else {
                localStorage.removeItem('token');
            }
        }
    });

    return {
        users,
        token,
        visitingUser,
        visitingToken,
        user,
        fetchVisitingUserAddresses,
        getTokenForUser,
        editTalent,
        newTalent,
        fetchUser,
        updateUser,
        deleteTalent,
        fetchUserProfile,
        fetchUserAddresses,
        loadUsers,
        loadAdmins,
        login,
        newContact,
        logOut,
        updateMe,
        loadUsersByCategory,
        setToken,
        searchUsers,
        changeOldPassword,
        changeEmail,
        updateUserOrder,
        setVisiting,
        loadUsersAdmin,
        loadUsersByCategoryAdmin,
        showLayout,
        setShowLayout
    };
}
